// lib/list/listOutputMedia.ts
import { sameListAssetFile } from './listOutputGroups';

export const SNAPSHOT_VERSION_NAMES = new Set([
  'Pre-Generation', 'Initial Quickstart Image', 'Auto ensure for annotation',
  'Auto ensure from project load: detached active version', 'Layer Visibility Toggle', 'Filters updated',
]);

type VideoLayer = {
  id: string; type?: string; videoSrc?: string | null; videoFramesUrls?: string[];
  visible?: boolean; topLayer?: boolean; renderOrder?: number;
};

/** Metadata fallback only: this does not prove which task produced a composed preview. */
export function resolveTopVisibleLayer<T extends VideoLayer>(layerVisibility: { id: string; visible: boolean }[] | undefined, layers: T[]): T | null {
  const visibility = new Map((layerVisibility ?? []).map(layer => [layer.id, layer.visible]));
  const visible = layers.filter(layer => visibility.get(layer.id) ?? layer.visible !== false);
  return [...(visible.length ? visible : layers)].sort((a, b) =>
    Number(Boolean(b.topLayer)) - Number(Boolean(a.topLayer)) || (b.renderOrder ?? 0) - (a.renderOrder ?? 0))[0] ?? null;
}

export function resolveVideoLayer<T extends VideoLayer>(
  layerVisibility: { id: string; visible: boolean }[] | undefined, layers: T[],
): T | null {
  const hasVisibilityList = Boolean(layerVisibility?.length);
  const visibility = new Map((layerVisibility ?? []).map(layer => [layer.id, layer.visible]));
  const visibleVideos = layers.filter(layer => {
    if (layer.type !== 'video' || (!layer.videoSrc && !layer.videoFramesUrls?.length)) return false;
    const override = visibility.get(layer.id);
    return override ?? (!hasVisibilityList && layer.visible !== false);
  });
  return visibleVideos.sort((a, b) => Number(Boolean(b.topLayer)) - Number(Boolean(a.topLayer))
    || (b.renderOrder ?? 0) - (a.renderOrder ?? 0))[0] ?? null;
}

export function resolveVersionVideoLayer<T extends VideoLayer>(
  version: { imageUrl?: string | null; layerVisibility?: { id: string; visible: boolean }[] }, layers: T[],
): T | null {
  const candidate = resolveVideoLayer(version.layerVisibility, layers);
  const frames = candidate?.videoFramesUrls?.filter(Boolean) ?? [];
  // Later images may snapshot an earlier video layer without owning its media.
  return candidate && (!version.imageUrl || !frames.length
    || frames.some(frame => sameListAssetFile(frame, version.imageUrl!))) ? candidate : null;
}
